import { z } from 'zod';
import {
    CloverPaymentResult,
    CloverTerminalSaleOutcome,
    type CloverTerminalSaleCardTransactionBody,
    type CloverTerminalSaleErrorBody,
    type CloverTerminalSalePaymentBody,
    type CloverTerminalSaleResponseDetails,
} from '../../../types/ipg/clover/clover-terminal-sale-response.types';
import { omitEmptyCloverCardTransactionText } from './clover-card-transaction.schemas';

type CloverTerminalSaleApprovedPaymentBody = CloverTerminalSalePaymentBody & {
    readonly id: string;
    readonly amount: number;
    readonly result: typeof CloverPaymentResult.SUCCESS;
};

type CloverTerminalSaleDeclinedPaymentBody = CloverTerminalSalePaymentBody & {
    readonly result: typeof CloverPaymentResult.FAIL;
};

export const CloverTerminalSaleCardTransactionBodyFieldsSchema: z.ZodObject<{
    authCode: z.ZodOptional<z.ZodString>;
    referenceId: z.ZodOptional<z.ZodString>;
    state: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    transactionNo: z.ZodOptional<z.ZodString>;
    type: z.ZodOptional<z.ZodString>;
}> = z.object({
    authCode: z.string().min(1).optional(),
    referenceId: z.string().min(1).optional(),
    state: z.string().min(1).optional(),
    token: z.string().min(1).optional(),
    transactionNo: z.string().min(1).optional(),
    type: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTerminalSaleCardTransactionBody>;

export const CloverTerminalSaleCardTransactionBodySchema: z.ZodType<CloverTerminalSaleCardTransactionBody> =
    z.preprocess(
        omitEmptyCloverCardTransactionText,
        CloverTerminalSaleCardTransactionBodyFieldsSchema
    );

export const CloverPaymentResultSchema: z.ZodType<CloverPaymentResult> = z.enum([
    CloverPaymentResult.SUCCESS,
    CloverPaymentResult.FAIL,
    CloverPaymentResult.INITIATED,
    CloverPaymentResult.VOIDED,
    CloverPaymentResult.VOIDING,
    CloverPaymentResult.VOID_FAILED,
    CloverPaymentResult.AUTH,
    CloverPaymentResult.AUTH_COMPLETED,
    CloverPaymentResult.DISCOUNT,
]);

export const CloverTerminalSalePaymentBodySchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    amount: z.ZodOptional<z.ZodNumber>;
    result: typeof CloverPaymentResultSchema;
    cardTransaction: z.ZodOptional<typeof CloverTerminalSaleCardTransactionBodySchema>;
    externalPaymentId: z.ZodOptional<z.ZodString>;
    externalReferenceId: z.ZodOptional<z.ZodString>;
}> = z.object({
    id: z.string().min(1).optional(),
    amount: z.number().int().min(1).max(999999999).optional(),
    result: CloverPaymentResultSchema,
    cardTransaction: CloverTerminalSaleCardTransactionBodySchema.optional(),
    externalPaymentId: z.string().min(1).optional(),
    externalReferenceId: z.string().min(1).max(12).optional(),
}) satisfies z.ZodType<CloverTerminalSalePaymentBody>;

export const CloverTerminalSaleApprovedPaymentBodySchema: z.ZodType<CloverTerminalSaleApprovedPaymentBody> =
    CloverTerminalSalePaymentBodySchema.extend({
        id: z.string().min(1),
        amount: z.number().int().min(1).max(999999999),
        result: z.literal(CloverPaymentResult.SUCCESS),
    });

export const CloverTerminalSaleDeclinedPaymentBodySchema: z.ZodType<CloverTerminalSaleDeclinedPaymentBody> =
    CloverTerminalSalePaymentBodySchema.extend({
        result: z.literal(CloverPaymentResult.FAIL),
    });

export const CloverTerminalSaleErrorBodySchema: z.ZodObject<{
    message: z.ZodString;
    type: z.ZodString;
    code: z.ZodOptional<z.ZodString>;
    requestId: z.ZodOptional<z.ZodString>;
    requestType: z.ZodOptional<z.ZodString>;
}> = z.object({
    message: z.string().min(1),
    type: z.string().min(1),
    code: z.string().min(1).optional(),
    requestId: z.string().min(1).optional(),
    requestType: z.string().min(1).optional(),
}) satisfies z.ZodType<CloverTerminalSaleErrorBody>;

export const CloverTerminalSaleApprovedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.APPROVED>;
    httpStatus: z.ZodLiteral<200>;
    body: z.ZodObject<{
        payment: typeof CloverTerminalSaleApprovedPaymentBodySchema;
    }>;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.APPROVED),
    httpStatus: z.literal(200),
    body: z.object({
        payment: CloverTerminalSaleApprovedPaymentBodySchema,
    }),
});

export const CloverTerminalSaleDeclinedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.DECLINED>;
    httpStatus: z.ZodLiteral<200>;
    body: z.ZodObject<{
        payment: typeof CloverTerminalSaleDeclinedPaymentBodySchema;
    }>;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.DECLINED),
    httpStatus: z.literal(200),
    body: z.object({
        payment: CloverTerminalSaleDeclinedPaymentBodySchema,
    }),
});

export const CloverTerminalSaleCanceledResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.CANCELED>;
    httpStatus: z.ZodLiteral<209>;
    body: typeof CloverTerminalSaleErrorBodySchema;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.CANCELED),
    httpStatus: z.literal(209),
    body: CloverTerminalSaleErrorBodySchema,
});

export const CloverTerminalSaleMalformedResponseDetailsSchema: z.ZodObject<{
    outcome: z.ZodLiteral<typeof CloverTerminalSaleOutcome.MALFORMED_RESPONSE>;
    httpStatus: z.ZodNumber;
    body: z.ZodUnknown;
    message: z.ZodString;
}> = z.object({
    outcome: z.literal(CloverTerminalSaleOutcome.MALFORMED_RESPONSE),
    httpStatus: z.number().int().positive(),
    body: z.unknown(),
    message: z.string().min(1),
});

export const CloverTerminalSaleResponseDetailsSchema: z.ZodType<CloverTerminalSaleResponseDetails> =
    z.discriminatedUnion('outcome', [
        CloverTerminalSaleApprovedResponseDetailsSchema,
        CloverTerminalSaleDeclinedResponseDetailsSchema,
        CloverTerminalSaleCanceledResponseDetailsSchema,
        CloverTerminalSaleMalformedResponseDetailsSchema,
    ]);
